import { DateHelper } from "@/Common/Helpers/DateHelper";
import { CareElementOperatorType } from "@/Flowsheets/Models/CareElementOperatorType";
import { CareElementFrequencyType } from "../Models/CareElementFrequencyType";
import { VitalSignExam } from "../Models/CareElements/VitalSignExam";
import { CareElementGridHelper } from "./CareElementGridHelper";
import { FrequencyDateHelper } from "./FrequencyDateHelper";

export class VitalSignCareElementGridHelper {
    /**
     * Checks if two dates are on the same day (year, month, date).
     */
    public static isSameDay(date1: Date, date2: Date): boolean {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }

    /**
     * Determines whether a frequency warning badge should be displayed.
     * @param frequency The care element frequency type.
     * @param encounterDate The date of the encounter (string or Date).
     * @param values A list of values corresponding to the dates.
     * @returns True if the warning badge should be displayed, otherwise false.
     */
    public static shouldShowFrequencyWarningBadge(
        frequency: CareElementFrequencyType,
        encounterDate: Date,
        values: VitalSignExam[]
    ): boolean {
        if (!encounterDate || frequency === CareElementFrequencyType.None || frequency === undefined) {
            return false;
        }

        const encounter = new Date(encounterDate);
        if (isNaN(encounter.getTime()))
            return false;

        const validExams = values.filter(vitalSign =>
            !IsNullOrUndefined(vitalSign.Value) || !IsNullOrUndefined(vitalSign.Value2)
        );

        const latestValidExam = validExams
            .filter(vitalSign => {
                const examDate = new Date(vitalSign.Date);
                return examDate < encounter || this.isSameDay(examDate, encounter);
            })
            .sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime())[0];

        if (!latestValidExam?.Date)
            return true;

        const latestDate = new Date(latestValidExam.Date);

        if (latestDate > encounter)
            return false;

        const minValidDate = FrequencyDateHelper.getMinValidDate(encounter, frequency);
        return !DateHelper.isInRange(latestDate, minValidDate, encounter);
    }

    /**
     * Determines if blood pressure values are out of range.
     */
    public static isBloodPressureOutOfRange(
        systolic: number,
        diastolic: number,
        systolicTarget: number,
        diastolicTarget: number,
        operator: CareElementOperatorType
    ): boolean {
        if (
            IsNullOrUndefined(systolic) || IsNullOrUndefined(diastolic) ||
            IsNullOrUndefined(systolicTarget) || IsNullOrUndefined(diastolicTarget) ||
            isNaN(systolic) || isNaN(diastolic) || isNaN(systolicTarget) || isNaN(diastolicTarget)
        ) {
            return false;
        }

        return (
            CareElementGridHelper.isOutOfRange(systolic, { LowerBound: systolicTarget, UpperBound: undefined }, operator) ||
            CareElementGridHelper.isOutOfRange(diastolic, { LowerBound: diastolicTarget, UpperBound: undefined }, operator)
        );
    }
}
